import { Component, inject, signal, Type } from '@angular/core';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Bell02 } from '../../icons/bell-02/bell-02';
import { Cube01 } from '../../icons/cube-01/cube-01';
import { AlertTriangle } from '../../icons/alert-triangle/alert-triangle';
import { Loading01 } from '../../icons/loading-01/loading-01';
import { Clock } from '../../icons/clock/clock';
import { Check } from '../../icons/check/check';
import { DynamicIconComponent } from '../../ui/dynamic-icon/dynamic-icon';
import { EyeOff } from '../../icons/eye-off/eye-off';
import { ChevronRight } from '../../icons/chevron-right/chevron-right';
import { File06 } from '../../icons/file-06/file-06';
import { NotificationService } from '../../services/notification.service';

export type NotificationIcon =
  | 'request'
  | 'warning'
  | 'invoice'
  | 'package'
  | 'reminder'
  | 'processing';

export interface NotificationItem {
  id: string;
  icon: NotificationIcon;
  title: string;
  description: string;
  timeAgo: string;
  unread: boolean;
  actionLabel?: string;
}

export const NOTIFICATION_ICON_MAP: Record<NotificationIcon, Type<any>> = {
  request: Cube01,
  warning: AlertTriangle,
  invoice: File06,
  package: Cube01,
  reminder: Clock,
  processing: Loading01,
};

@Component({
  standalone: true,
  selector: 'app-notifications',
  imports: [
    Tabs,
    TabList,
    Tab,
    Bell02,
    EyeOff,
    Check,
    ChevronRight,
    DynamicIconComponent,
    Paginator,
  ],
  templateUrl: './notifications.html',
  styleUrl: './notifications.scss',
})
export class NotificationsComponent {
  protected notificationService = inject(NotificationService);

  protected items = this.notificationService.items;
  protected paginatedData = this.notificationService.paginatedData;
  protected first = this.notificationService.first;

  protected activeTab = signal<number>(0);

  constructor() {
    this.notificationService.loadNotifications();
    this.notificationService.loadUnreadCount();
  }

  protected iconFor(type: NotificationIcon): Type<any> {
    return NOTIFICATION_ICON_MAP[type];
  }

  protected onTabChange(value: unknown) {
    const tab = Number(value ?? 0);
    this.activeTab.set(tab);
    this.notificationService.loadNotifications(tab === 1);
  }

  protected onPageChange(event: PaginatorState) {
    this.notificationService.onPageChange({
      first: event.first ?? 0,
      rows: event.rows ?? this.paginatedData().pageSize,
    });
  }

  protected markAllAsRead() {
    this.notificationService.markAllAsRead();
  }

  protected markAsRead(id: string) {
    this.notificationService.markAsRead(id);
  }
}
